/**
 * Contrato v1 do resumo de concentração do cockpit do FIC seguido.
 *
 * Espelha o payload do backend UQ-4530
 * (`GET /meta-carteiras-fic-fidcs/:operationId/concentration`), view-model
 * único que alimenta os cards Top 1/5/10/25 e o gráfico resumido por fundo.
 * O backend é o dono dos valores: o numerador de cada Top é a soma acumulada
 * de `vlMercPosFinal` dos FIDCs elegíveis até o rank e o denominador é o
 * `vlPatrimLiq` do FIC pai na competência autoritativa. O frontend não
 * recalcula participação, não agrega fatias por conta própria e não faz
 * fetch por fundo.
 */

export const FIC_FIDC_COCKPIT_CONCENTRATION_VERSION = 1;

export const CONCENTRATION_RANKS = [1, 5, 10, 25] as const;

export type ConcentrationRank = (typeof CONCENTRATION_RANKS)[number];

/** Estado da competência reportado pelo backend. */
export type FicFidcCockpitConcentrationStatus =
  | 'available'
  | 'partial'
  | 'empty'
  | 'unavailable'
  | 'error';

/** Razão do estado reportada pelo backend. */
export type FicFidcCockpitConcentrationReason =
  | 'none'
  | 'empty'
  | 'missing-position'
  | 'zero-total'
  | 'no-competence-data'
  | 'missing-metadata'
  | 'source-error';

export type FicFidcCompositionCardKey = 'asset' | 'manager';

export type FicFidcCompositionCardStatus =
  | 'available'
  | 'partial'
  | 'empty'
  | 'unavailable'
  | 'error';

export interface FicFidcGroupedCompositionItem {
  name: string;
  fundCount: number;
  vlMercPosFinal: number;
  percentage: number;
}

export interface FicFidcGroupedCompositionMeta {
  status: FicFidcCompositionCardStatus;
  reason:
    | 'none'
    | 'missing-position'
    | 'empty'
    | 'no-competence-data'
    | 'zero-total'
    | 'missing-metadata'
    | 'source-error';
  competence: string | null;
  coverage: {
    fundCount: number;
    eligibleFundCount: number;
    missingPositionCount: number;
    missingMetadataCount: number;
  };
}

export interface FicFidcGroupedCompositionViewModel {
  data: FicFidcGroupedCompositionItem[];
  meta: FicFidcGroupedCompositionMeta;
}

export interface FicFidcCompositionCardViewModel {
  key: FicFidcCompositionCardKey;
  label: string;
  amount: number | null;
  percentage: number | null;
  status: FicFidcCompositionCardStatus;
  reason: FicFidcCockpitConcentrationReason;
  competence: string | null;
  coverage: {
    total: number;
    withMetadata: number;
  };
  items: FicFidcGroupedCompositionItem[];
}

export interface FicFidcCockpitConcentrationV1Position {
  rank: number;
  name: string | null;
  cnpj: string;
  operationId: string | null;
  slug: string | null;
  qtPosFinal: number | null;
  vlMercPosFinal: number;
  /** Participação (%) sobre o PL do FIC pai na competência. */
  percentage: number | null;
  cumulativeVlMercPosFinal: number;
  cumulativePercentage: number | null;
}

export interface FicFidcCockpitConcentrationV1Point {
  top: ConcentrationRank;
  /** false significa rank sem fundo: rank e valores são null (N/D). */
  available: boolean;
  rank: number | null;
  cumulativeVlMercPosFinal: number | null;
  cumulativePercentage: number | null;
}

export interface FicFidcCockpitConcentrationV1Payload {
  version: typeof FIC_FIDC_COCKPIT_CONCENTRATION_VERSION;
  data: FicFidcCockpitConcentrationV1Position[];
  meta: {
    status: FicFidcCockpitConcentrationStatus;
    reason: FicFidcCockpitConcentrationReason;
    /** Competência solicitada, ecoada como âncora YYYY-MM-01; null sem date. */
    requestedCompetence: string | null;
    /** Competência efetiva dos dados; null quando a competência não tem dados. */
    competence: string | null;
    parent: {
      cnpj: string | null;
      vlPatrimLiq: number | null;
    };
    coverage: {
      fundCount: number;
      eligibleFundCount: number;
      missingPositionCount: number;
    };
    cards: {
      top1: FicFidcCockpitConcentrationV1Point;
      top5: FicFidcCockpitConcentrationV1Point;
      top10: FicFidcCockpitConcentrationV1Point;
      top25: FicFidcCockpitConcentrationV1Point;
    };
    markers: FicFidcCockpitConcentrationV1Point[];
    grouped?: {
      ativoLastro: FicFidcGroupedCompositionViewModel;
      gestor: FicFidcGroupedCompositionViewModel;
    };
  };
}
